<template>
  <div class="f-h gw-stats">
    <FiltersZone :modal-title="$t('message.OPTIONS')" @open="isPanelOpen = true" @close="isPanelOpen = false">
      <div slot="shortcuts" class="v-centerer">
        <ChartPeriodSelector
          v-model="displayConfig"
          :preset-id="presetId"
          query-prefix="gwStats"
          :hide-preset-field="$store.state.device.isSmartphoneLandscape"
          @presetPick="presetId = $event"
        />

        <div class="v-centerer ml10em">
          <span v-if="!$store.state.device.isSmartphoneLandscape" class="legend mr10">
            {{ $t("message.DISPLAY") + $t("message.TWO_DOTS") }}
          </span>
          <Switcher
            v-if="!$store.state.device.isSmartphonePortrait"
            v-model="chartTypeIdx"
            class="gw-stats__switcher"
            :titles="chartTypeTitles"
          />
        </div>
      </div>

      <ComponentsList
        ref="componentsList"
        :value="gwStatsOptions"
        @edit="onEditOption"
      />
    </FiltersZone>

    <div class="f-h-below-filters">
      <div class="gw-stats__charts">
        <div class="gw-stats__charts__row">
          <div class="gw-stats__charts__row__wrapper">
            <div class="gw-stats__charts__row__wrapper__subwrapper">
              <h2 class="name">
                {{ $t('message.GW_PLANNING_CHART') }}
              </h2>
              <GwPlanningCompliance
                class="canvas-wrapper"
                :type="chartType"
                :teams="gwStatsOptTeamFilter"
                :templates="gwStatsOptTemplateFilter"
                :range="visibleValuesRange"
                :working-days="allTeamsWorkingDays"
                :hide-non-working-days="gwStatsHideNonWD === 1"
                :display-config="displayConfig"
              />
            </div>
          </div>
        </div>
        <div class="gw-stats__charts__row">
          <div class="gw-stats__charts__row__wrapper">
            <div class="gw-stats__charts__row__wrapper__subwrapper">
              <h2 class="name">
                {{ $t('message.GW_ITEMS_OK_CHART') }}
              </h2>
              <GwChecklistItemsChart
                class="canvas-wrapper"
                :type="chartType"
                :stats-data="chartData"
                :cl-items="gwStatsOptClItems"
                :working-days="allTeamsWorkingDays"
                :hide-non-working-days="gwStatsHideNonWD === 1"
                :visible-chart-range="visibleValuesRange"
                :display-config="displayConfig"
              />
            </div>
          </div>
        </div>
        <div class="gw-stats__charts__row">
          <div class="gw-stats__charts__row__wrapper">
            <div class="gw-stats__charts__row__wrapper__subwrapper">
              <h2 class="name">
                {{ $t('message.GW_NOK_ITEMS_PARETO') }}
              </h2>
              <GwNOKItemsChart
                class="canvas-wrapper"
                :gw-data="gwData"
              />
            </div>
          </div>
          <div class="gw-stats__charts__row__wrapper">
            <div class="gw-stats__charts__row__wrapper__subwrapper">
              <h2 class="name">
                {{ $t('message.GW_NOK_ITEMS_PARETO_ZONES') }}
              </h2>
              <GwNOKItemsChart
                class="canvas-wrapper"
                :gw-data="gwData"
                :group-by="1"
              />
            </div>
          </div>
          <div class="gw-stats__charts__row__wrapper">
            <div class="gw-stats__charts__row__wrapper__subwrapper">
              <h2 class="name">
                {{ $t('message.GW_NOK_ITEMS_PARETO_TEAMS') }}
              </h2>
              <GwNOKItemsChart
                class="canvas-wrapper"
                :gw-data="gwData"
                :group-by="2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Gewba walks statistics view
 */

import { isEqual } from 'lodash';
// Vuex
import { GET_TEAMS } from '@/store/mutation-types';
// Import components
import ChartPeriodSelector from '@/components/chart-period-selector.vue';
import ComponentsList from '@/components/components-list.vue';
import FiltersZone from '@/components/filters-zone.vue';
import GwChecklistItemsChart from '@/components/gw-checklist-items-chart.vue';
import GwNOKItemsChart from '@/components/gw-nok-items-chart.vue';
import GwPlanningCompliance from '@/components/gw-planning-compliance.vue';
import Switcher from '@/components/switcher.vue';
// Import mixins
import mixinGwStatsOptions from '@/mixins/page-options/gw-stats.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Import utils
import SocketMessages from '@/utils/classes/SocketMessages';
import { CL_ITEMS_CHART_TYPES } from '@/utils/gw-stats';
import {
  nestedFindItemWithId,
  treeAsArray,
  parseWorkingDays,
  calcPeriodStartDate,
} from '@/utils/main';
import { computeConfigStart } from '@/utils/indicator-utils';


export default {

  name: 'GwStats',

  components: {
    ChartPeriodSelector,
    ComponentsList,
    FiltersZone,
    GwChecklistItemsChart,
    GwNOKItemsChart,
    GwPlanningCompliance,
    Switcher,
  },

  mixins: [mixinGwStatsOptions, mixinSocketHandler],

  data() {
    return {
      chartData: {},
      gwData: [],
      displayConfig: this.$store.getters.indicDisplayConfigs.find(config => config.id === 5),
      // This data's purpose is to refresh componentsList $refs when opening and closing the options
      isPanelOpen: false,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.GW_STATS'),
    };
  },

  computed: {
    chartTypeTitles() {
      return CL_ITEMS_CHART_TYPES.map(t => this.$t(t.i18nLabel));
    },
    chartType() {
      return CL_ITEMS_CHART_TYPES[this.chartTypeIdx].type;
    },
    fetchParams() {
      return {
        completed: true,
        includeData: false,
        range: {
          start: this.allValuesRange.start.format('YYYY-MM-DD HH:mm:ss'),
          end: this.allValuesRange.end.format('YYYY-MM-DD HH:mm:ss'),
        },
        zones: this.gwStatsOptZoneAll === 1 ? this.getZones(this.gwStatsOptZoneFilter) : undefined,
        teams: this.gwStatsOptTeamAll === 1 ? this.gwStatsOptTeamFilter : undefined,
        checklists: this.gwStatsOptChecklistAll === 1 ? this.gwStatsOptChecklistFilter : undefined,
        templates: (this.gwStatsOptTemplateAll === 1 && this.gwStatsOptTemplateFilter.length > 0
          ? this.gwStatsOptTemplateFilter
          : undefined),
      };
    },
    /** Teams from vuex */
    allTeams() {
      return this.$store.state[GET_TEAMS.stateKey] || [];
    },
    /** Merge all working days of fetched teams */
    allTeamsWorkingDays() {
      const teams = this.gwStatsOptTeamAll === 0
        ? this.allTeams
        : this.allTeams.filter(team => this.gwStatsOptTeamFilter.includes(team.id));
      const allWorkingDays = teams.reduce((acc, team) => acc.concat(team.workingDays), []);
      return parseWorkingDays(allWorkingDays.filter((v, idx, a) => a.indexOf(v) === idx));
    },
    // Queries
    chartTypeIdx: {
      get() {
        const chartType = Number(this.$route.query.chartType);
        if (!CL_ITEMS_CHART_TYPES[chartType]) {
          return 1;
        }
        return chartType;
      },
      set(newValue) {
        const query = Object.assign({}, this.$route.query, { chartType: newValue });
        this.$router.replace({ query });
      },
    },
    /** Object: Range of chart's visible values */
    visibleValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      const { chartPeriodObj, pointPeriodObj, chartEnd } = this.displayConfig;
      return {
        start: calcPeriodStartDate(chartEnd, chartPeriodObj, pointPeriodObj),
        end: chartEnd.clone().endOf('day'),
      };
    },
    /**
     * Object: Range of all values, including values not displayed.
     * Especially useful when building a cumulated chart (where increasingValues > 0).
     * eg: Values should be cumulated each month from the 1st of each month
     *     => need the values from the 1st day of month
     */
    allValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      if (!this.displayConfig.increasingValues) {
        // No increasing values, no need for an extended range
        return this.visibleValuesRange;
      }
      const { chartEnd, configStart } = computeConfigStart(this.displayConfig, this.visibleValuesRange.start);
      return { start: this.$moment(configStart), end: chartEnd };
    },
    /** Indicator config ID */
    presetId: {
      get() {
        const { presetId, defaultPeriod } = this.$route.query;
        if (presetId === null || isNaN(presetId)) {
          // No default preset in query, use default weekly indicator config
          if (defaultPeriod === null || isNaN(defaultPeriod)) {
            return 5;
          }
          return defaultPeriod === null || isNaN(defaultPeriod) ? 1 : Number(defaultPeriod);
        }
        return Number(presetId);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query, { presetId: newVal });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    // mixinGwStatsOptions overrides
    componentsListRefs() {
      // Refresh the value when opening and closing, otherwise all $refs become undefined upon closing
      /* eslint-disable-next-line no-unused-expressions */
      this.isPanelOpen;
      return this.$refs.componentsList.$refs;
    },
    gwStatsHideNonWD() {
      return Number(this.$route.query.hideNonWD);
    },
    gwStatsNonWDNumber() {
      return this.allTeamsWorkingDays.filter(v => !v).length;
    },
    gwStatsOptClItems() {
      const { clItems } = this.$route.query;
      if (clItems === undefined) return [0, 1, 2];
      return clItems ? clItems.split(',').map(n => Number(n)) : [];
    },
    gwStatsOptZoneAll() {
      const { zoneFilter } = this.$route.query;
      return zoneFilter !== undefined ? 1 : 0;
    },
    gwStatsOptZoneFilter() {
      const { zoneFilter } = this.$route.query;
      if (!zoneFilter && zoneFilter !== '0') return 0;
      const zoneId = Number(zoneFilter);
      return zoneId > 0 ? zoneId : 0;
    },
    gwStatsOptTeamAll() {
      const { teamFilter } = this.$route.query;
      return teamFilter !== undefined ? 1 : 0;
    },
    gwStatsOptTeamFilter() {
      const { teamFilter } = this.$route.query;
      if (!teamFilter) {
        return this.$store.getters.teamsTreeCurrentFlattened.map(team => team.id);
      }
      const teams = teamFilter.split(',').map(id => Number(id));
      return teams;
    },
    gwStatsOptChecklistAll() {
      const { checklistFilter } = this.$route.query;
      return checklistFilter !== undefined ? 1 : 0;
    },
    gwStatsOptChecklistFilter() {
      const { checklistFilter } = this.$route.query;
      if (!checklistFilter) return [];
      const checklists = checklistFilter.split(',').map(id => Number(id));
      return checklists;
    },
    gwStatsOptTemplateAll() {
      const { templateFilter } = this.$route.query;
      return templateFilter !== undefined ? 1 : 0;
    },
    gwStatsOptTemplateFilter() {
      const { templateFilter } = this.$route.query;
      if (!templateFilter) return [];
      const templates = templateFilter.split(',').map(id => Number(id));
      return templates;
    },
  },

  watch: {
    fetchParams() {
      this.refresh();
    },
  },

  created() {
    this.refresh();
    this.handleSocketMessage(SocketMessages.CC_MSGS.GW_END, this.refresh);
  },

  methods: {
    /**
     * Get all childs of a given zone id
     * @param {Number} zoneId - ID of a zone
     * @return {Number[]} List of zone ids, zoneId included
     */
    getZones(zoneId) {
      const zone = nestedFindItemWithId(this.gwStatsOptZonesData, zoneId, 'zones');
      return zone ? [zoneId, ...treeAsArray(zone.zones, 'zones').map(z => z.id)] : [];
    },
    /**
     * Fetch and store GW stats data according to filters
     * Parameters are set with the computed property `fetchParams`
     */
    refresh() {
      this.$http.post('/server/gembaWalks/getStats.php', this.fetchParams).then(({ data }) => {
        if (data instanceof Object) {
          Object.freeze(data);
          this.chartData = data.data;
        }
      });
      this.$http.post('/server/gembaWalks/get.php', this.fetchParams).then(({ data }) => {
        Object.freeze(data);
        this.gwData = data;
      });
    },
    /**
     * Event handler ; triggered by ComponentsList edition
     * Set query according to the new given option
     * @param {Object} option - Contains a key/value pair of the edited option
     */
    onEditOption(option) {
      const query = Object.assign({}, this.$route.query);
      switch (option.key) {
        case 'defaultPeriod':
          query.presetId = Number(option.value);
          break;
        case 'hideNonWD':
          query.hideNonWD = option.value;
          break;
        case 'clItems':
          query.clItems = option.value.join(',');
          break;
        case 'zoneAll':
          if (option.value === 0) {
            delete query.zoneFilter;
          } else if (option.value === 1 && query.zoneFilter === undefined) {
            query.zoneFilter = '';
          }
          break;
        case 'zoneFilter':
          query.zoneFilter = String(option.value);
          break;
        case 'teamAll':
          if (option.value === 0) {
            delete query.teamFilter;
          } else if (option.value === 1 && query.teamFilter === undefined) {
            query.teamFilter = '';
          }
          break;
        case 'teamFilter':
          query.teamFilter = option.value.join(',');
          break;
        case 'checklistAll':
          if (option.value === 0) {
            delete query.checklistFilter;
          } else if (option.value === 1 && query.checklistFilter === undefined) {
            query.checklistFilter = '';
          }
          break;
        case 'checklistFilter':
          query.checklistFilter = option.value.join(',');
          break;
        case 'templateAll':
          if (option.value === 0) {
            delete query.templateFilter;
          } else if (option.value === 1 && query.templateFilter === undefined) {
            query.templateFilter = '';
          }
          break;
        case 'templateFilter':
          query.templateFilter = option.value.join(',');
          break;
        default:
          // No query change ; No need to $router.replace
          return;
      }
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    /**
     * mixinGwStatsOptions override
     */
    gwStatsOptConfigChanged(option) {
      this.onEditOption(option);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-stats {
  &__switcher.switcher {
    height: 2.1rem;
  }

  .f-h-below-filters {
    padding: $page-content-padding $page-content-padding 0 0;
  }

  &__charts {
    height: 100%;

    &__row {
      height: 100%;
      width: 100%;

      &:not(:first-child) {
        margin-top: $page-content-padding;
      }

      // While not on smartphon-landscape (see below), adapt the height of chart rows
      // so all rows fit in the entire page. Same behaviour for chart-wrapper.
      // Basically, lines below make two rows height = 50%, three rows = 33%
      &:first-child:nth-last-child(2), &:first-child:nth-last-child(2) ~ .gw-stats__charts__row {
        height: calc(50% - #{$page-content-padding});
      }
      &:first-child:nth-last-child(3), &:first-child:nth-last-child(3) ~ .gw-stats__charts__row {
        height: calc(33.3333% - #{$page-content-padding});
      }
      &__wrapper {
        display: inline-block;
        width: calc(100% - #{$page-content-padding});
        height: 100%;
        box-sizing: border-box;
        vertical-align: top;
        margin-left: $page-content-padding;

        &__subwrapper {
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          padding: 5px;
          background: #ffffff;
          border-radius: $main-border-radius;

          h2.name {
            height: 3rem;
            line-height: 3rem;
            text-align: center;
            font-weight: 600;
            margin: 0;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
          .canvas-wrapper {
            height: calc(100% - 3rem);
          }
        }

        // Just as chart-row, adapt the width of chart wrappers so they all fit in the page
        &:first-child:nth-last-child(2), &:first-child:nth-last-child(2) ~ .gw-stats__charts__row__wrapper {
          width: calc(50% - #{$page-content-padding});
        }
        &:first-child:nth-last-child(3), &:first-child:nth-last-child(3) ~ .gw-stats__charts__row__wrapper {
          width: calc(33.3333% - #{$page-content-padding});
        }
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .gw-stats {
    &__charts {
      &__row {
        // Unset the height to let the children (.gw-stats__charts__row__wrapper) occupy 90% of the page height
        &:first-child:nth-last-child(2), &:first-child:nth-last-child(2) ~ .gw-stats__charts__row {
          height: auto;
        }
        &:first-child:nth-last-child(3), &:first-child:nth-last-child(3) ~ .gw-stats__charts__row {
          height: auto;
        }
        &__wrapper {

          &:first-child:nth-last-child(2), &:first-child:nth-last-child(2) ~ .gw-stats__charts__row__wrapper {
            width: calc(100% - #{$page-content-padding});
            margin-bottom: $page-content-padding;
          }
          &:first-child:nth-last-child(3), &:first-child:nth-last-child(3) ~ .gw-stats__charts__row__wrapper {
            width: calc(100% - #{$page-content-padding});
            margin-bottom: $page-content-padding;
          }
        }
      }
    }
  }
}
</style>
